import { AnimeGridSkeleton } from '@/components/ui/Skeleton';
import { ErrorState, StateMessage } from '@/components/ui/StateMessage';
import { AnimeCard } from './AnimeCard';
import type { AnimeSummary } from '@/types/anime';
import type { ReactNode } from 'react';

interface AnimeGridProps {
	items: AnimeSummary[];
	isLoading?: boolean;
	error?: unknown;
	onRetry?: () => void;
	skeletonCount?: number;
	emptyTitle?: string;
	emptyDescription?: ReactNode;
	emptyAction?: ReactNode;
	/** Có truyền là chuyển nút góc phải của thẻ sang chế độ "xoá". */
	onRemove?: (anime: AnimeSummary) => void;
	/** Làm mờ lưới khi đang tải trang kế (giữ nội dung cũ thay vì skeleton). */
	isRefreshing?: boolean;
}

export function AnimeGrid({
	items,
	isLoading = false,
	error,
	onRetry,
	skeletonCount = 12,
	emptyTitle = 'Không tìm thấy anime phù hợp',
	emptyDescription = 'Thử đổi từ khoá hoặc bỏ bớt bộ lọc xem sao.',
	emptyAction,
	onRemove,
	isRefreshing = false,
}: AnimeGridProps) {
	if (isLoading) return <AnimeGridSkeleton count={skeletonCount} />;
	if (error) return <ErrorState error={error} onRetry={onRetry} />;

	if (items.length === 0) {
		return (
			<StateMessage
				icon="🔎"
				title={emptyTitle}
				description={emptyDescription}
				action={emptyAction}
			/>
		);
	}

	return (
		<div
			className={`grid grid-cols-2 items-start gap-4 transition-opacity min-[700px]:grid-cols-4 min-[1100px]:grid-cols-6 ${
				isRefreshing ? 'opacity-55' : 'opacity-100'
			}`}
		>
			{items.map((anime, index) => (
				<AnimeCard
					key={anime.slug}
					anime={anime}
					eager={index < 6}
					{...(onRemove ? { onRemove: () => onRemove(anime) } : {})}
				/>
			))}
		</div>
	);
}
